
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminUsersTable } from "@/components/admin/users/AdminUsersTable";
import { useSimpleAuth } from "@/hooks/useSimpleAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserForm } from "@/components/admin/users/UserForm";

interface User {
  id: string;
  email: string;
  name: string | null;
  active: boolean;
  access_level: 'usuario' | 'admin';
}

type DatabaseUser = {
  id: string;
  email: string;
  name: string | null;
  access_level: string;
  active: boolean;
  password: string;
  created_at: string;
  updated_at: string;
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user: currentUser } = useSimpleAuth();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const validateAccessLevel = (level: string): 'usuario' | 'admin' => {
    return level === 'admin' ? 'admin' : 'usuario';
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('cat_users')
        .select('*')
        .order('name');
      
      if (error) throw error;

      const formattedUsers: User[] = (data as DatabaseUser[]).map(dbUser => ({
        id: dbUser.id,
        email: dbUser.email,
        name: dbUser.name,
        active: dbUser.active,
        access_level: validateAccessLevel(dbUser.access_level)
      }));

      setUsers(formattedUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (currentUser?.access_level !== 'admin') {
      toast.error('Apenas administradores podem excluir usuários');
      return;
    }

    if (!confirm(`Deseja realmente excluir o usuário ${user.name || user.email}?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('cat_users')
        .delete()
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Usuário excluído com sucesso');
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar />
      
      <div className="flex-1 ml-[250px]"> {/* Adicionado margin-left igual à largura do sidebar */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Usuários</h1>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
              <Button 
                onClick={handleNewUser}
                className="flex items-center gap-2 bg-blue-700 hover:bg-blue-600"
              >
                <PlusCircle className="w-4 h-4" />
                Novo Usuário
              </Button>
            </div>
          </div>

          <div className="max-w-[1200px]">
            {isLoading ? (
              <div>Carregando...</div>
            ) : (
              <AdminUsersTable 
                users={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            )}
          </div>
        </div>

        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <UserForm
              initialData={selectedUser ? {
                ...selectedUser,
                password: '', // Adicionamos um password vazio para edição
              } : undefined}
              onClose={() => setIsFormOpen(false)}
              onSuccess={fetchUsers}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
